import {createRouter as createVueRouter, createWebHistory} from 'vue-router';
import store from '../store/index';
const { decideRoute } = require('./decideRoute');

const PUBLIC_PREFIXES = ['/login', '/verify', '/passwordReset'];

function classFor(to) {
    // explizites meta.authClass gewinnt
    const metaClass = to.matched.reduce((acc, r) => r.meta?.authClass || acc, null);
    if (metaClass) return metaClass;
    if (to.path.startsWith('/onboarding')) return 'onboarding';
    if (PUBLIC_PREFIXES.some(p => to.path === p || to.path.startsWith(p))) return 'public';
    return 'protected';
}

function hasSessionCookie() {
    return document.cookie.split(';').some(c => c.trim().startsWith('sessionKey='));
}

function createRoutes() {
    const routes = [];
    // Auth-Views (public)
    routes.push({ path: '/login', meta: { authClass: 'public' },
        components: { default: () => import(/* webpackChunkName: "auth" */ '../../components/views/auth/LoginView.vue') } });
    routes.push({ path: '/passwordReset', meta: { authClass: 'public' },
        components: { default: () => import(/* webpackChunkName: "auth" */ '../../components/views/auth/PasswordResetRequestView.vue') } });
    routes.push({ path: '/passwordReset/:token', meta: { authClass: 'public' },
        components: { default: () => import(/* webpackChunkName: "auth" */ '../../components/views/auth/PasswordResetView.vue') } });
    // public: nach der Registrierung ist der Nutzer noch NICHT eingeloggt (E-Mail erst
    // per 6-stelligem Code bestaetigen) -- diese Seite muss ohne Cookie erreichbar sein.
    // step:2 nur fuer den Wizard-Fortschritt.
    routes.push({ path: '/verify-pending', meta: { authClass: 'public', step: 2 },
        components: { default: () => import(/* webpackChunkName: "onboarding" */ '../../components/views/onboarding/VerifyPendingStep.vue') } });

    // App-Routen-Module + Onboarding
    const pages = ['dashboard','library','generator','screens','apps','playlists','users','settings','profile','releaseRequests','billing','onboarding','verbinden'];
    for (const page of pages) {
        try { routes.push(require('./' + page + '.js')); }
        catch (err) { console.warn(err.message); }
    }

    routes.push({ path: '/:catchAll(.*)', meta: { authClass: 'protected' },
        components: { default: () => import(/* webpackChunkName: "notFound" */ '../../components/views/notFound.vue') } });
    return routes;
}

// Hinweis: main.js ruft `createRouter(app)` — das Extra-Argument wird hier ignoriert
// (Signatur ohne Parameter). main.js MUSS nicht angepasst werden, der Aufruf bleibt gueltig.
export default () => {
    const router = createVueRouter({
        history: createWebHistory(process.env.BASE_URL),
        routes: createRoutes(),
    });

    router.beforeEach(async (to, from, next) => {
        // 0. Impersonation-Token aus der URL zuerst (B4b)
        const params = new URLSearchParams(window.location.search);
        const impToken = params.get('impersonationToken');
        if (impToken) {
            // Secure (nur HTTPS) + SameSite=Lax: Lax, weil der Token per Top-Level-Navigation
            // aus dem Betreiber-Cockpit (andere Origin) kommt -- Strict wuerde das Cookie beim
            // ersten Aufruf evtl. nicht senden. Auf localhost (http) Secure weglassen, sonst
            // wuerde der Browser das Cookie verwerfen.
            const secure = window.location.protocol === 'https:' ? ' Secure;' : '';
            document.cookie = 'sessionKey=' + encodeURIComponent(impToken) + '; path=/; SameSite=Lax;' + secure;
            store.state.reevaluateLogin++;
            params.delete('impersonationToken');
            const q = params.toString();
            window.history.replaceState({}, document.title, window.location.pathname + (q ? '?' + q : ''));
            store.commit('clearAccount'); // status frisch laden
        }

        let decision = decideRoute({
            hasCookie: hasSessionCookie(),
            status: store.state.tenantStatus,
            targetPath: to.path,
            targetClass: classFor(to),
            isImpersonation: store.state.isImpersonation,
        });

        if (decision.action === 'loadStatus') {
            await store.dispatch('loadAccountStatus');
            decision = decideRoute({
                hasCookie: hasSessionCookie(),
                status: store.state.tenantStatus,
                targetPath: to.path,
                targetClass: classFor(to),
                isImpersonation: store.state.isImpersonation,
            });
        }

        if (decision.action === 'loadStatus') {
            // Status nach dem Laden immer noch unbekannt (Cookie ungueltig / 403) -> nicht-eingeloggt
            document.cookie = 'sessionKey=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
            store.commit('clearAccount');
            if (to.path === '/login') return next();
            return next({ path: '/login', query: { redirect: to.fullPath } });
        }

        if (decision.action === 'redirect' && decision.path !== to.path) {
            // Deep-Link-Ziel (z.B. /verbinden?code=) ueber den Login retten -- nur fuer
            // geschuetzte Ziele, interne Pfade erzwingt der LoginView-Check.
            if (decision.path === '/login' && classFor(to) === 'protected') {
                return next({ path: '/login', query: { redirect: to.fullPath } });
            }
            return next(decision.path);
        }
        return next();
    });

    return router;
}
